/**
 * Painel de status do ambiente (AIH-09).
 *
 * Consulta `/readyz` e mostra o estado de cada dependência. Existe desde a Fase
 * 1 e continua sendo a primeira coisa a olhar quando algo não sobe.
 *
 * O portfólio (AIH-37) toma este lugar em `/` quando existir. Até lá, é esta a
 * página que responde na raiz.
 */
import { useCallback, useEffect, useState } from 'react';
import { usePageMeta } from '../hooks/usePageMeta.js';
import { Header } from '../components/Header.jsx';
import { Panel } from '../components/Panel.jsx';

const CHECK_LABELS = {
  redis: 'Redis — sessão e cache',
  // Rótulo sem o nome do produto: a implementação por baixo é RabbitMQ ou
  // Azure Service Bus, escolhida por configuração, e a tela não deveria mudar
  // quando a infraestrutura muda. Qual está valendo vem em `driver`.
  fila: 'Fila de mensagens',
  mysql: 'MySQL — catálogo e log',
  entra: 'Microsoft Entra ID — autenticação',
  servicenow: 'ServiceNow — chamados',
};

const STATUS_LABELS = {
  ok: { cls: 'ok', text: 'ok' },
  configurado: { cls: 'ok', text: 'configurado' },
  falha: { cls: 'fail', text: 'falha' },
  nao_configurado: { cls: 'na', text: 'não configurado' },
};

function CheckRow({ name, state }) {
  const spec = STATUS_LABELS[state] ?? { cls: '', text: state };
  return (
    <li>
      <span className={`dot ${spec.cls}`} />
      <span className="name">{name}</span>
      <span className={`state ${spec.cls}`}>{spec.text}</span>
    </li>
  );
}

export function Status() {
  usePageMeta('AI Hub — status do ambiente');

  const [checks, setChecks] = useState(null);
  const [meta, setMeta] = useState('');
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/readyz', { headers: { accept: 'application/json' } });
      const data = await res.json();
      setChecks(data.checks ?? {});
      setMeta(
        `${data.status} · verificado em ${data.durationMs} ms · ` +
          new Date().toLocaleTimeString('pt-BR'),
      );
    } catch (err) {
      // A API não respondeu. Não é o mesmo que uma dependência em falha, e a
      // tela precisa dizer qual dos dois é — senão manda procurar no lugar errado.
      setChecks({ api: 'falha' });
      setMeta(String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <>
      <Header badge="fase 1 · fundação" />

      <main>
        <h1>Status do ambiente</h1>
        <p className="lead">
          Esta página consulta <code>/readyz</code> e mostra o state de cada dependência. O
          portfólio entra na Etapa 2.
        </p>

        <Panel
          title="Dependências"
          action={
            <button type="button" onClick={refresh} disabled={loading}>
              verificar
            </button>
          }
          rodape={meta}
        >
          <ul className="checks">
            {loading && !checks && <li className="empty">consultando…</li>}
            {checks &&
              Object.entries(checks).map(([key, value]) => (
                <CheckRow
                  key={key}
                  name={key === 'api' ? 'API inacessível' : (CHECK_LABELS[key] ?? key)}
                  state={value}
                />
              ))}
          </ul>
        </Panel>

        <Panel
          title="Próximas etapas"
          rodape={
            <>
              Plano completo em <code>documentation/README-APP.md</code>.
            </>
          }
        >
          <ol className="steps">
            <li>
              <b>Etapa 1</b> — autenticação com Entra ID <span className="tag">pronta</span>
            </li>
            <li>
              <b>Etapa 2</b> — banco de dados MySQL <span className="tag">em curso</span>
            </li>
            <li>
              <b>Etapa 3</b> — conexão com o ServiceNow <span className="tag">planejado</span>
            </li>
          </ol>
        </Panel>
      </main>
    </>
  );
}
